import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import type { KeyboardEvent } from 'react';
import { useEffect, useState } from 'react';

import { Button, Text } from '@revelio/ui';

import { DataBuilderGrid } from '../../components/ui/DataBuilderGrid';
import { DataBuilderOption } from '../../components/ui/DataBuilderOption';
import type { DataBuilderSettingValue } from '../../data-builder.store';
import type { DeliverableSetting } from '../../datasets/deliverable-column-config';

const sectionStyles = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: '12px',
};

type SettingsSectionProps = {
  settings: DeliverableSetting[];
  values: Record<string, DataBuilderSettingValue>;
  onSetSetting: (key: string, value: DataBuilderSettingValue) => void;
};

type SettingCardProps = {
  detail: string;
  indicator?: 'checkbox' | 'radio';
  isSelected: boolean;
  onClick: () => void;
  testId: string;
  title: string;
};

const SettingCard = ({
  detail,
  indicator = 'checkbox',
  isSelected,
  onClick,
  testId,
  title,
}: SettingCardProps) => {
  return (
    <DataBuilderOption
      data-testid={testId}
      isSelected={isSelected}
      onClick={onClick}
      startSlot={
        indicator === 'radio' ? (
          <DataBuilderOption.Radio isSelected={isSelected} />
        ) : (
          <DataBuilderOption.CheckBox isSelected={isSelected} />
        )
      }
    >
      <DataBuilderOption.Label>{title}</DataBuilderOption.Label>
      {detail && (
        <DataBuilderOption.Description>{detail}</DataBuilderOption.Description>
      )}
    </DataBuilderOption>
  );
};

type SettingGroupCardProps = {
  detail?: string;
  isSelected: boolean;
  onClick: () => void;
  onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
  testId: string;
  title: string;
  totalCount: number;
};

const SettingGroupCard = ({
  detail: detailOverride,
  isSelected,
  onClick,
  onKeyDown,
  testId,
  title,
  totalCount,
}: SettingGroupCardProps) => {
  const selectedCount = isSelected ? 1 : 0;
  const detail =
    detailOverride !== undefined
      ? detailOverride
      : `${selectedCount}/${totalCount} selected`;

  return (
    <DataBuilderOption
      data-testid={testId}
      isSelected={isSelected}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={onKeyDown}
      startSlot={<DataBuilderOption.CheckBox isSelected={isSelected} />}
      endSlot={<DataBuilderOption.Chevron />}
      endSlotAlignment="center"
    >
      <DataBuilderOption.Label>{title}</DataBuilderOption.Label>
      {detail && (
        <DataBuilderOption.Description>{detail}</DataBuilderOption.Description>
      )}
    </DataBuilderOption>
  );
};

const toggleDisclosure = (
  isOpen: boolean,
  onClose: () => void,
  onOpen: () => void
) => {
  if (isOpen) {
    onClose();
    return;
  }

  onOpen();
};

type SettingToggleCardProps = {
  setting: Extract<DeliverableSetting, { type: 'toggle' }>;
  value: DataBuilderSettingValue | undefined;
  onSetSetting: (key: string, value: DataBuilderSettingValue) => void;
};

const SettingToggleCard = ({
  setting,
  value,
  onSetSetting,
}: SettingToggleCardProps) => {
  const isSelected = typeof value === 'boolean' ? value : setting.default;

  return (
    <SettingCard
      title={setting.name}
      detail={setting.description ?? ''}
      isSelected={isSelected}
      onClick={() => onSetSetting(setting.key, !isSelected)}
      testId={`data-builder-setting-toggle-${setting.key}`}
    />
  );
};

type SettingInlineGroupProps = {
  setting: Extract<DeliverableSetting, { type: 'selection_inline' }>;
  value: DataBuilderSettingValue | undefined;
  onSetSetting: (key: string, value: DataBuilderSettingValue) => void;
};

type SettingInlineItem = Extract<
  DeliverableSetting,
  { type: 'selection_inline' }
>['items'][number];
type SettingSelectionItem = Extract<
  DeliverableSetting,
  { type: 'selection' }
>['items'][number];

const SettingInlineGroup = ({
  setting,
  value,
  onSetSetting,
}: SettingInlineGroupProps) => {
  const selectedValue = typeof value === 'string' ? value : setting.default;

  return (
    <div
      style={{
        ...sectionStyles,
        gridColumn: '1 / -1',
      }}
    >
      <DataBuilderGrid columns="responsive">
        {setting.items.map((item: SettingInlineItem) => (
          <SettingCard
            key={item.value}
            indicator="radio"
            title={item.name}
            detail={item.description ?? ''}
            isSelected={selectedValue === item.value}
            onClick={() => onSetSetting(setting.key, item.value)}
            testId={`data-builder-setting-inline-${setting.key}-${item.value}`}
          />
        ))}
      </DataBuilderGrid>
    </div>
  );
};

type SettingSelectionModalProps = {
  setting: Extract<DeliverableSetting, { type: 'selection' }>;
  value: DataBuilderSettingValue | undefined;
  onSetSetting: (key: string, value: DataBuilderSettingValue) => void;
};

const SettingSelectionModal = ({
  setting,
  value,
  onSetSetting,
}: SettingSelectionModalProps) => {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const selectedValue = typeof value === 'string' ? value : setting.default;
  const [draftValue, setDraftValue] = useState(selectedValue);
  const selectedItem =
    setting.items.find(
      (item: SettingSelectionItem) => item.value === selectedValue
    ) ??
    setting.items.find(
      (item: SettingSelectionItem) => item.value === setting.default
    ) ??
    setting.items[0];

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setDraftValue(selectedValue);
  }, [isOpen, selectedValue]);

  return (
    <>
      <SettingGroupCard
        detail={selectedItem?.name}
        isSelected={selectedItem !== undefined}
        onClick={() => toggleDisclosure(isOpen, onClose, onOpen)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' && event.key !== ' ') {
            return;
          }

          event.preventDefault();
          toggleDisclosure(isOpen, onClose, onOpen);
        }}
        testId={`data-builder-setting-select-${setting.key}`}
        title={setting.name}
        totalCount={setting.items.length}
      />
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
        <ModalOverlay />
        <ModalContent
          data-testid={`data-builder-setting-select-modal-${setting.key}`}
        >
          <ModalHeader color="text.primary" fontWeight="semibold" fontSize="md">
            {setting.name}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <DataBuilderGrid columns="2">
              {setting.items.map((item: SettingSelectionItem) => (
                <SettingCard
                  key={item.value}
                  indicator="radio"
                  title={item.name}
                  detail={item.description ?? ''}
                  isSelected={draftValue === item.value}
                  onClick={() => setDraftValue(item.value)}
                  testId={`data-builder-setting-select-${setting.key}-${item.value}`}
                />
              ))}
            </DataBuilderGrid>
          </ModalBody>
          <ModalFooter>
            <Button
              color="primary"
              size="sm"
              onClick={() => {
                onSetSetting(setting.key, draftValue);
                onClose();
              }}
              data-testid={`data-builder-setting-select-submit-${setting.key}`}
            >
              Submit
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export const SettingsSection = ({
  settings,
  values,
  onSetSetting,
}: SettingsSectionProps) => {
  if (settings.length === 0) {
    return null;
  }

  return (
    <section style={sectionStyles} data-testid="data-builder-settings">
      <Text as="h3" fontSize="sm" weight="semibold">
        Settings
      </Text>
      <DataBuilderGrid columns="responsive">
        {settings.map((setting) => {
          const value = values[setting.key];

          if (setting.type === 'toggle') {
            return (
              <SettingToggleCard
                key={setting.key}
                setting={setting}
                value={value}
                onSetSetting={onSetSetting}
              />
            );
          }

          if (setting.type === 'selection_inline') {
            return (
              <SettingInlineGroup
                key={setting.key}
                setting={setting}
                value={value}
                onSetSetting={onSetSetting}
              />
            );
          }

          return (
            <SettingSelectionModal
              key={setting.key}
              setting={setting}
              value={value}
              onSetSetting={onSetSetting}
            />
          );
        })}
      </DataBuilderGrid>
    </section>
  );
};
